/**
 * RoMSH 입력 기록 표·상태·EvidenceCard 줄의 문구 — PLAN 2-9a PR-3.
 *
 * 정확한 문자열 SSOT: docs/2026/260923_RoMSH_입력기록_기획.md §3-3·§3-4.
 * 금지 문구 점검(§3-6)이 이 파일 전부를 검사한다. 문구를 바꾸면 기획 문서도
 * 함께 바꾸고, 금지 취지(인과·중요도·판정·수치 해석·순위·보장)에 비추어
 * 사람이 한 번 더 읽는다.
 *
 * `{…}` 자리는 기록에서 읽은 값(데이터)으로 채운다 — 화면에 규칙 이름·버전을
 * 박지 않는다.
 */

export const ROMSH_INPUT_TEXT = {
  title: "RoMSH 입력 성분 — 모델에 보낸 성분의 기록",
  titleSide: "분석 시점 기록 · 결과 해시 대상 아님",
  summaryOver: (N: number, n: number, rule: string) =>
    `구조 정보가 있는 후보 물질 ${N}종(같은 이름은 1건만) 중 ${n}개 입력 · 선정 규칙 ${rule}`,
  summaryWithin: (N: number, rule: string) =>
    `구조 정보가 있는 후보 물질 ${N}종 전부 입력 · 선정 규칙 ${rule}`,
  summaryExcluded: (k: number) => ` · 구조 정보 없는 ${k}종 제외`,
  subtitle:
    "각 성분이 모델 판정에 미친 영향을 나타내지 않습니다. 행 순서는 모델에 보낸 순서(RT 순)입니다.",
  subtitleAllZeroRt:
    "각 성분이 모델 판정에 미친 영향을 나타내지 않습니다. 행 순서는 모델에 보낸 순서(기록된 순서)입니다.",
  columns: {
    rt: "RT(분)",
    name: "물질명",
    observation: "관찰",
    jsd: "JSD 거리",
    reason: "선정",
    rule: "탄화수소 구조 규칙",
  },
  reason: {
    all_within_limit: "전량(8개 이하)",
    abundance_reserve: "거리순 6개",
    rt_bin_fill: "RT 구간 보충",
    remaining_fill: "남은 자리 보충",
  } as Record<string, string>,
  ruleMet: "충족",
  ruleNotMet: "미충족",
  none: "—",
  observation: (peakNo: number) => `관찰 ${peakNo}`,
  rtMissing: "(RT 없음 — 0 으로 보냄)",
  ruleGuide:
    "탄화수소 구조 규칙은 분자 구조만 보는 계산 규칙입니다. '미충족'이어도 인화성이 없다는 뜻이 아니고, '충족'이어도 인화성 물질이라는 뜻이 아닙니다.",
  titleInfoV21: (withJsd: boolean) =>
    "RoMSH 는 한 번에 최대 8개 성분을 입력으로 받습니다. 넣을 성분은 플랫폼이 고릅니다. 기록된 후보 가운데 같은 이름은 매칭 점수가 가장 높은 1건만 남기고(점수가 같으면 신호가 큰 관찰, 그것도 같으면 먼저 기록된 것), 구조(SMILES)가 없는 것은 뺍니다. " +
    (withJsd
      ? "매칭 점수는 JSD 거리로 계산하며, 거리가 가까울수록 높습니다. "
      : "") +
    "남은 수가 8개보다 많으면 매칭 점수가 높은 순으로 6개를 먼저 고릅니다(점수가 같으면 먼저 기록된 물질이 앞섭니다). 남은 두 자리는 아직 고른 성분이 없는 RT 구간에서 채우고, 그런 구간이 없으면 매칭 점수가 높은 순으로 채웁니다. RT 구간은 2.1~4.5 · 4.5~6.9 · 6.9~9.2 · 9.2~11.6 · 11.6~14.0 · 14.0~16.3 · 16.3~18.7 · 18.7~21.1분의 여덟 칸이며, 2.1분보다 앞은 첫 칸, 21.1분보다 뒤는 마지막 칸으로 셉니다(플랫폼 선정 규칙 v2.1).",
  titleInfoOther: (rule: string) =>
    `넣을 성분은 플랫폼이 고릅니다(플랫폼 선정 규칙 ${rule}).`,
  jsdInfo:
    "참조 스펙트럼과의 거리(동정 단계 값)입니다. 작을수록 가깝습니다. RoMSH 입력값은 아닙니다. RoMSH 는 성분마다 이 거리로 계산한 매칭 점수를 받습니다(점수 값은 표시하지 않습니다).",
  reasonInfo:
    "이 성분이 8개 안에 들어온 경로입니다. '거리순'은 매칭 점수 순입니다. 소수 넷째 자리까지 같은 점수는 먼저 기록된 순서로 정합니다.",
  ruleInfo:
    "분자 구조(SMILES)에 탄소·수소 외의 원자가 없고 탄소가 3개 이상이면 '충족'입니다. 모델 입력값 fuel_proxy 를 만드는 계산 규칙입니다.",
  summaryValuesLabel: "모델 입력 요약값",
  envFixedSuffix: "(플랫폼 고정)",
  summaryInfoAbundance:
    "AI 서비스가 모델에 실제로 넣은 값을 되돌려 받아 기록한 것입니다. fuel_proxy 는 입력 성분 가운데 탄화수소 구조 규칙 '충족' 성분의 매칭 점수 합을 전체 매칭 점수 합으로 나눈 값(0~1)입니다. fuel_proxy · ab_entropy_norm · dominant_abundance 는 매칭 점수로 계산한 값입니다. 시료 속 함량이나 성분 비중이 아닙니다. mix_norm 은 입력 성분 수, rt_span_norm 은 RT 폭으로 계산합니다. env_id 는 플랫폼이 모든 분석에 0 을 넣습니다.",
  summaryInfoOther:
    "AI 서비스가 모델에 실제로 넣은 값을 되돌려 받아 기록한 것입니다. 이 분석의 fuel_proxy 는 매칭 점수로 나누지 않고 성분 수로 나눈 값입니다(균등 계산). SMILES 에 공백이 있으면 공백으로 나뉜 조각마다 규칙을 적용해 세므로, 위 표의 '충족' 표시와 맞지 않을 수 있습니다. ab_entropy_norm · dominant_abundance 는 매칭 점수로 계산한 값입니다. 시료 속 함량이나 성분 비중이 아닙니다. mix_norm 은 입력 성분 수, rt_span_norm 은 RT 폭으로 계산합니다. env_id 는 플랫폼이 모든 분석에 0 을 넣습니다.",
  // 2026-09-25 추가(기획 §3-3): 요청에 실린 fuel_proxy 를 AI 가 그대로 쓴 경우.
  // 플랫폼 워크플로는 지금 fuel_proxy 를 보내지 않는다(재검증 스크립트 등에서만).
  summaryInfoRequest:
    "AI 서비스가 모델에 실제로 넣은 값을 되돌려 받아 기록한 것입니다. 이 분석의 fuel_proxy 는 성분에서 계산하지 않고 분류 요청에 실린 값을 그대로 쓴 것입니다. ab_entropy_norm · dominant_abundance 는 매칭 점수로 계산한 값입니다. 시료 속 함량이나 성분 비중이 아닙니다. mix_norm 은 입력 성분 수, rt_span_norm 은 RT 폭으로 계산합니다. env_id 는 플랫폼이 모든 분석에 0 을 넣습니다.",
  selectGuide:
    "행을 누르면 아래 후보 표와 질량 스펙트럼에서 그 후보가 선택됩니다.",
  footnote1:
    "표에 없는 후보는 모델 입력이 아닙니다. 모델은 스펙트럼이나 봉우리 세기를 받지 않습니다. 성분마다 구조(SMILES)·RT·매칭 점수를 받고, 이것으로 계산한 요약값과 env_id 를 함께 받습니다.",
  footnote1End: "위 '우세 후보 단서'는 전체 후보 집계로 이 표와 다릅니다.",
  footnote2:
    "이 표는 모델에 보낸 성분의 기록입니다. 성분의 실제 존재나 함량, 화재 원인을 나타내지 않습니다.",
  dupSmiles: (k: number) => `(같은 SMILES ${k}회)`,
  dupSmilesInfo: (k: number) =>
    `같은 SMILES 문자열이 라이브러리의 서로 다른 이름 항목으로 ${k}번 들어갔습니다. 모델은 이름이 아니라 SMILES 를 받으므로 같은 입력을 ${k}번 받았습니다.`,
} as const;

/** §3-4 상태별 문구 — 표 **위**에 둔다. */
export const ROMSH_INPUT_STATE_TEXT = {
  noSnapshot:
    "RoMSH 입력 성분 — 기록 없음. 이 분석에는 증거 스냅숏이 없습니다. 소급 기입하지 않습니다.",
  noRecord:
    "RoMSH 입력 성분 — 기록 없음. 이 분석에는 RoMSH 입력 기록이 남아 있지 않습니다. 소급 기입하지 않습니다.",
  recordError: (code: string) =>
    `RoMSH 입력 기록을 남기지 못했습니다(오류 ${code}). RoMSH 결과 값은 이 기록과 따로 저장됩니다.`,
  unreadable: (schema: string) =>
    `RoMSH 입력 기록 형식을 읽을 수 없습니다(${schema}).`,
  build: (code: string) =>
    `모델 요청을 만들지 못해 보내지 않았습니다(${code}).`,
  circuit:
    "AI 서비스 호출이 잠시 차단된 상태라 모델 요청을 보내지 않았습니다. 아래는 보내려던 입력입니다.",
  transport: (code: string) =>
    `AI 서비스 호출 중 오류가 나 응답을 확인하지 못했습니다(${code}). 아래는 보내려던 입력이며, 모델이 받았는지는 확인되지 않았습니다.`,
  clientParse:
    "AI 서비스의 응답을 백엔드가 읽지 못했습니다(응답 해석 실패). 아래는 보낸 입력입니다. 탄화수소 구조 규칙과 모델 입력 요약값은 표시하지 않습니다.",
  noModelResult: (why: string) =>
    `모델 결과를 받지 못했습니다(${why}). 아래는 보낸 입력이며, 모델이 받은 값은 돌아오지 않았습니다.`,
  unknownStatus:
    "모델 결과를 받지 못했습니다(알 수 없는 상태값). 아래는 보낸 입력입니다.",
  noModelResultWhy: {
    failed: "모델 실행 실패",
    model_unavailable: "모델 미로드",
    ai_http: "AI 서비스 오류 응답",
  } as Record<string, string>,
  echoAbsent:
    "아래는 보낸 입력입니다. 모델 쪽 값이 전달되지 않아 탄화수소 구조 규칙과 모델 입력 요약값은 표시하지 않습니다.",
  echoInvalid:
    "아래는 보낸 입력입니다. 모델 쪽 값을 읽을 수 없어 탄화수소 구조 규칙과 모델 입력 요약값은 표시하지 않습니다.",
  echoMismatch:
    "아래는 보낸 입력입니다. AI 서비스가 되돌린 입력이 보낸 입력과 맞지 않습니다. 위 모델 판정이 이 표의 입력으로 계산됐는지 확인되지 않았습니다. 탄화수소 구조 규칙과 모델 입력 요약값은 표시하지 않습니다.",
} as const;

/** 분석 상세 EvidenceCard 「RoMSH 입력 기록」 줄 (§3-3 끝 표). */
export const ROMSH_INPUT_EVIDENCE_TEXT = {
  label: "RoMSH 입력 기록",
  noRecord: "기록 없음",
  recordError: (code: string) => `기록 실패(오류 ${code})`,
  unreadable: (schema: string) => `형식을 읽을 수 없음(${schema})`,
  selectStopped: "입력 성분 없음 — 모델을 실행하지 않음",
  notSent: (code: string) => `모델 요청을 보내지 않음(${code})`,
  transport: (n: number, code: string) =>
    `보내려던 ${n}성분 · AI 서비스 응답을 확인하지 못함(${code})`,
  clientParse: (n: number) => `${n}성분 · 백엔드가 AI 응답을 읽지 못함`,
  noModelResult: (n: number, why: string) =>
    `${n}성분 · 모델 결과 없음(${why})`,
  echoAbsent: (n: number) => `${n}성분 · 모델 쪽 값이 전달되지 않음`,
  echoInvalid: (n: number) => `${n}성분 · 모델 쪽 값을 읽을 수 없음`,
  echoMismatch: (n: number) =>
    `${n}성분 · AI 서비스가 되돌린 입력이 보낸 입력과 맞지 않음`,
  ok: (n: number, N: number, rule: string, v: string) =>
    `${n}성분(구조 정보가 있는 후보 물질 ${N}종 중) · 선정 규칙 ${rule} · 기록 ${v}`,
  unknownStatusWhy: "알 수 없는 상태값",
} as const;

/**
 * romsh_input_v2 · 알려진 출처 abundance_source=tic_height_fraction 의 문구 — PLAN 2-9d.
 *
 * 계약: docs/2026/260930_RoMSH_TIC높이_구현계약.md「입력 기록과 화면」. 저장된
 * 기록의 schema·출처로만 고른다(지금 서버 설정으로 고르지 않는다). 위 v1 문구는
 * 당시 매칭 점수 설명 그대로 둔다.
 *
 * 금지 문구 점검(§3-6)을 이 객체에도 적용한다. 승인된 예외는 칸 이름
 * 「TIC 신호 비율」 하나이고, 백분율 기호는 데이터 칸에만 쓴다.
 */
export const ROMSH_INPUT_HEIGHT_TEXT = {
  summaryOver: (N: number, n: number, rule: string) =>
    `대표 관찰 ${N}건 중 ${n}개 입력 · 선정 규칙 ${rule}`,
  summaryWithin: (N: number, rule: string) =>
    `대표 관찰 ${N}건 전부 입력 · 선정 규칙 ${rule}`,
  summaryNotRepresentative: (m: number) => ` · 입력에서 뺀 후보 ${m}건`,
  /** selection.excluded[].reason — 백엔드 romsh_abundance 의 사유 값. */
  excludedReason: {
    lower_rank_alternative: "같은 스캔의 다른 후보",
    no_smiles: "대표 후보에 구조 정보 없음",
    range_average: "RT 범위 평균 스캔",
    duplicate_tic_point: "같은 TIC 지점을 가리킨 다른 스캔",
    duplicate_smiles: "같은 SMILES 의 TIC 높이가 더 작은 관찰",
  } as Record<string, string>,
  excludedOther: "그 밖의 사유",
  excludedInfo: (parts: string) =>
    `입력에서 뺀 후보의 사유: ${parts}. 이 후보들은 모델 입력이 아닙니다.`,
  /** 선택 단계에서 멈춘 v2 기록 가운데 건너뜀(no_winners·no_smiles)이 아닌 것. */
  selectFailed: (code: string) =>
    `RoMSH 입력 성분을 정하지 못해 모델을 실행하지 않았습니다(${code}).`,
  evidenceSelectFailed: (code: string) =>
    `입력 성분을 정하지 못해 모델을 실행하지 않음(${code})`,
  titleInfo: (rule: string) =>
    "RoMSH 는 한 번에 최대 8개 성분을 입력으로 받습니다. 넣을 성분은 플랫폼이 고릅니다. 스캔마다 가장 앞선 후보 하나를 대표로 삼고, 그 후보에 구조(SMILES)가 없으면 그 관찰은 뺍니다. 같은 TIC 지점은 한 번만 쓰고, 같은 SMILES 가 여러 RT 에 있으면 TIC 높이가 가장 큰 관찰 하나만 남깁니다. " +
    "남은 수가 8개보다 많으면 TIC 신호가 큰 순으로 6개를 먼저 고릅니다. 남은 두 자리는 아직 고른 성분이 없는 RT 구간에서 채우고, 그런 구간이 없으면 TIC 신호가 큰 순으로 채웁니다. 모델에 보내는 값은 고른 성분의 TIC 높이를 그 합으로 나눈 TIC 신호 비율입니다. " +
    `이 계산 방식은 운영자가 승인한 작업가설이며, 모델 학습 때의 산출식과 같은지는 아직 확인하지 못했습니다(플랫폼 선정 규칙 ${rule}).`,
  columnSignal: "TIC 신호 비율",
  signalInfo:
    "모델에 보낸 값입니다. 이 성분의 TIC 높이를 입력 성분 전체의 TIC 높이 합으로 나눈 값을 백분율로 적었습니다. 측정 신호의 상대 크기일 뿐, 확정된 양이나 농도가 아닙니다.",
  jsdInfo:
    "참조 스펙트럼과의 거리(동정 단계 값)입니다. 작을수록 가깝습니다. RoMSH 입력값은 아닙니다. 양이나 농도를 나타내지 않습니다.",
  reason: {
    all_within_limit: "전량(8개 이하)",
    abundance_reserve: "신호순 6개",
    rt_bin_fill: "RT 구간 보충",
    remaining_fill: "남은 자리 보충",
  } as Record<string, string>,
  reasonInfo:
    "이 성분이 8개 안에 들어온 경로입니다. '신호순'은 TIC 신호가 큰 순입니다.",
  summaryInfo: {
    abundance:
      "AI 서비스가 모델에 실제로 넣은 값을 되돌려 받아 기록한 것입니다. fuel_proxy 는 입력 성분 가운데 탄화수소 구조 규칙 '충족' 성분의 TIC 신호 비율 합(0~1)입니다. fuel_proxy · ab_entropy_norm · dominant_abundance 는 TIC 신호 비율로 계산한 값입니다. 시료 속 함량이나 성분 비중이 아닙니다. mix_norm 은 입력 성분 수, rt_span_norm 은 RT 폭으로 계산합니다. env_id 는 플랫폼이 모든 분석에 0 을 넣습니다.",
    uniform:
      "AI 서비스가 모델에 실제로 넣은 값을 되돌려 받아 기록한 것입니다. 이 분석의 fuel_proxy 는 TIC 신호 비율로 나누지 않고 성분 수로 나눈 값입니다(균등 계산). SMILES 에 공백이 있으면 공백으로 나뉜 조각마다 규칙을 적용해 세므로, 위 표의 '충족' 표시와 맞지 않을 수 있습니다. ab_entropy_norm · dominant_abundance 는 TIC 신호 비율로 계산한 값입니다. 시료 속 함량이나 성분 비중이 아닙니다. mix_norm 은 입력 성분 수, rt_span_norm 은 RT 폭으로 계산합니다. env_id 는 플랫폼이 모든 분석에 0 을 넣습니다.",
    request:
      "AI 서비스가 모델에 실제로 넣은 값을 되돌려 받아 기록한 것입니다. 이 분석의 fuel_proxy 는 성분에서 계산하지 않고 분류 요청에 실린 값을 그대로 쓴 것입니다. ab_entropy_norm · dominant_abundance 는 TIC 신호 비율로 계산한 값입니다. 시료 속 함량이나 성분 비중이 아닙니다. mix_norm 은 입력 성분 수, rt_span_norm 은 RT 폭으로 계산합니다. env_id 는 플랫폼이 모든 분석에 0 을 넣습니다.",
  },
  footnote1:
    "표에 없는 후보는 모델 입력이 아닙니다. 모델은 스펙트럼을 받지 않습니다. 성분마다 구조(SMILES)·RT·TIC 신호 비율을 받고, 이것으로 계산한 요약값과 env_id 를 함께 받습니다.",
  footnoteSignal:
    "TIC 신호 비율은 측정 신호의 상대 크기일 뿐, 확정된 양이나 농도가 아닙니다.",
  evidenceOk: (n: number, N: number, rule: string, v: string) =>
    `${n}성분(대표 관찰 ${N}건 중) · 선정 규칙 ${rule} · 기록 ${v}`,
} as const;

/**
 * 이 화면이 모르는 abundance_source 의 중립 문구 — PLAN 2-9d.
 *
 * 매칭 점수라고도, TIC 라고도 부르지 않는다. 규칙 이름이 v2.1 이어도 v1 매칭
 * 점수 문구를 빌려 오지 않는다. 입력값 칸(비율)은 그리지 않는다.
 */
export const ROMSH_INPUT_NEUTRAL_TEXT = {
  summaryOver: (N: number, n: number, rule: string) =>
    `기록된 후보 ${N}건 중 ${n}개 입력 · 선정 규칙 ${rule}`,
  summaryWithin: (N: number, rule: string) =>
    `기록된 후보 ${N}건 전부 입력 · 선정 규칙 ${rule}`,
  summaryExcluded: (k: number) => ` · 구조 정보 없는 ${k}건 제외`,
  titleInfo: (rule: string) =>
    `RoMSH 는 한 번에 최대 8개 성분을 입력으로 받습니다. 넣을 성분은 플랫폼이 고릅니다(플랫폼 선정 규칙 ${rule}). 이 기록의 입력값(abundance) 출처는 이 화면이 설명하는 형식이 아니어서, 입력값을 표에 보이지 않습니다.`,
  jsdInfo:
    "참조 스펙트럼과의 거리(동정 단계 값)입니다. 작을수록 가깝습니다. RoMSH 입력값은 아닙니다.",
  reason: {
    all_within_limit: "전량(8개 이하)",
    abundance_reserve: "입력값순 6개",
    rt_bin_fill: "RT 구간 보충",
    remaining_fill: "남은 자리 보충",
  } as Record<string, string>,
  reasonInfo:
    "이 성분이 8개 안에 들어온 경로입니다. '입력값순'은 기록된 입력값(abundance)으로 먼저 고른 6개입니다.",
  summaryInfo: {
    abundance:
      "AI 서비스가 모델에 실제로 넣은 값을 되돌려 받아 기록한 것입니다. fuel_proxy · ab_entropy_norm · dominant_abundance 는 기록된 입력값(abundance)으로 계산한 값입니다. 시료 속 함량이나 성분 비중이 아닙니다. mix_norm 은 입력 성분 수, rt_span_norm 은 RT 폭으로 계산합니다. env_id 는 플랫폼이 모든 분석에 0 을 넣습니다.",
    uniform:
      "AI 서비스가 모델에 실제로 넣은 값을 되돌려 받아 기록한 것입니다. 이 분석의 fuel_proxy 는 입력값으로 나누지 않고 성분 수로 나눈 값입니다(균등 계산). SMILES 에 공백이 있으면 공백으로 나뉜 조각마다 규칙을 적용해 세므로, 위 표의 '충족' 표시와 맞지 않을 수 있습니다. ab_entropy_norm · dominant_abundance 는 기록된 입력값(abundance)으로 계산한 값입니다. 시료 속 함량이나 성분 비중이 아닙니다. mix_norm 은 입력 성분 수, rt_span_norm 은 RT 폭으로 계산합니다. env_id 는 플랫폼이 모든 분석에 0 을 넣습니다.",
    request:
      "AI 서비스가 모델에 실제로 넣은 값을 되돌려 받아 기록한 것입니다. 이 분석의 fuel_proxy 는 성분에서 계산하지 않고 분류 요청에 실린 값을 그대로 쓴 것입니다. ab_entropy_norm · dominant_abundance 는 기록된 입력값(abundance)으로 계산한 값입니다. 시료 속 함량이나 성분 비중이 아닙니다. mix_norm 은 입력 성분 수, rt_span_norm 은 RT 폭으로 계산합니다. env_id 는 플랫폼이 모든 분석에 0 을 넣습니다.",
  },
  footnote1:
    "표에 없는 후보는 모델 입력이 아닙니다. 모델은 스펙트럼을 받지 않습니다. 성분마다 구조(SMILES)·RT·기록된 입력값(abundance)을 받고, 이것으로 계산한 요약값과 env_id 를 함께 받습니다.",
  evidenceOk: (n: number, N: number, rule: string, v: string) =>
    `${n}성분(기록된 후보 ${N}건 중) · 선정 규칙 ${rule} · 기록 ${v}`,
} as const;
